import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import { api } from "../lib/api";
import ChatPanel from "../components/ChatPanel";
import { Hash, ArrowLeft } from "lucide-react";
import { Avatar } from "../components/ui-bits";
import { formatDistanceToNow } from "date-fns";
import { cn } from "../lib/utils";

export const dmChannel = (a, b) => `dm_${[a, b].sort().join("_")}`;

export default function Chat() {
  const { user } = useAuth();
  const { users } = useData();
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState("general"); // "general" or a user id
  const [summary, setSummary] = useState([]);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);

  const selectChannel = (id) => {
    setSelected(id);
    setMobileChatOpen(true);
  };

  useEffect(() => {
    const dm = searchParams.get("dm");
    if (dm) setSelected(dm);
  }, [searchParams, setSelected]);

  // api is a module-level import (non-reactive); setSummary is a stable state setter.
  const loadSummary = useCallback(async () => {
    const { data } = await api.get("/chat/dm/summary");
    setSummary(data);
  }, [setSummary]);

  useEffect(() => {
    loadSummary().catch(() => {});
    const t = setInterval(() => loadSummary().catch(() => {}), 15000);
    return () => clearInterval(t);
  }, [loadSummary]);

  const others = users.filter((u) => u.id !== user?.id);
  const summaryFor = (uid) => summary.find((s) => s.channel === dmChannel(user.id, uid));
  const channel = selected === "general" ? "general" : dmChannel(user.id, selected);
  const selectedUser = users.find((u) => u.id === selected);

  return (
    <div className="h-full flex p-3 md:p-6 gap-4" data-testid="chat-page">
      <div className={cn("w-full md:w-64 shrink-0 bg-card border border-border rounded-lg flex-col overflow-hidden", mobileChatOpen ? "hidden md:flex" : "flex")} data-testid="dm-sidebar">
        <p className="px-3 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Messages</p>
        <div className="px-1.5">
          <button data-testid="chat-channel-general" onClick={() => selectChannel("general")}
            className={cn("w-full flex items-center gap-2 px-2 py-2 rounded-md text-left transition-colors", selected === "general" ? "bg-[#E31E24]/10 text-[#E31E24]" : "hover:bg-muted")}>
            <span className="h-6 w-6 rounded-md bg-[#1B2A6B]/10 text-navy dark:text-blue-300 flex items-center justify-center shrink-0"><Hash size={13} /></span>
            <span className="text-sm font-medium truncate">General chat</span>
          </button>
        </div>

        <p className="px-3 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Direct messages · {others.length}</p>
        <div className="flex-1 overflow-y-auto px-1.5 pb-2 space-y-0.5" data-testid="dm-users-list">
          {others.length === 0 && <p className="text-xs text-muted-foreground px-2 py-3">No other members yet. Add teammates from the Team page.</p>}
          {others.map((u) => {
            const s = summaryFor(u.id);
            return (
              <button key={u.id} data-testid={`dm-user-${u.id}`} onClick={() => selectChannel(u.id)}
                className={cn("w-full flex items-center gap-2 px-2 py-2 rounded-md text-left transition-colors", selected === u.id ? "bg-[#E31E24]/10" : "hover:bg-muted")}>
                <Avatar name={u.name} userId={u.id} online={u.online} />
                <div className="flex-1 min-w-0">
                  <p className={cn("text-sm font-medium truncate", selected === u.id && "text-[#E31E24]")}>{u.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {s ? `${s.last_message.user_id === user.id ? "You: " : ""}${s.last_message.text || "Sent an attachment"}` : "Start a conversation"}
                  </p>
                </div>
                {s && <span className="text-[9px] text-muted-foreground shrink-0">{formatDistanceToNow(new Date(s.last_message.created_at))}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className={cn("flex-1 min-w-0 bg-card border border-border rounded-lg flex-col overflow-hidden", mobileChatOpen ? "flex" : "hidden md:flex")}>
        <div className="h-12 px-3 md:px-4 border-b border-border flex items-center gap-2.5 shrink-0" data-testid="chat-header">
          <button data-testid="chat-back-button" onClick={() => setMobileChatOpen(false)}
            className="md:hidden h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0">
            <ArrowLeft size={16} />
          </button>
          {selected === "general" ? (
            <>
              <span className="h-7 w-7 rounded-md bg-[#1B2A6B]/10 text-navy dark:text-blue-300 flex items-center justify-center"><Hash size={14} /></span>
              <div>
                <p className="text-sm font-heading font-bold leading-tight">General chat</p>
                <p className="text-[10px] text-muted-foreground leading-tight">Everyone in the workspace</p>
              </div>
            </>
          ) : (
            <>
              <Avatar name={selectedUser?.name || "?"} userId={selectedUser?.id} online={selectedUser?.online} />
              <div>
                <p className="text-sm font-heading font-bold leading-tight" data-testid="dm-header-name">{selectedUser?.name || "Direct message"}</p>
                <p className="text-[10px] leading-tight flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: selectedUser?.online ? "#22C55E" : "#EF4444" }} />
                  <span className="text-muted-foreground">{selectedUser?.online ? "Online" : "Offline"}</span>
                </p>
              </div>
            </>
          )}
        </div>
        <div className="flex-1 min-h-0 p-3">
          <ChatPanel channel={channel} className="h-full" testid={selected === "general" ? "general-chat" : "dm-chat"} />
        </div>
      </div>
    </div>
  );
}
